import { Option } from "api/types/_public";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownCategoryVote from "components/Common/DropdownCategoryVote";
import LabelWrapper from "components/Common/LabelWrapper";
import { SECTION_KEYWORD } from "helpers/constans";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, Card, CardBody, Col, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { getAINewsByNormal, postReCreateVoteKeywordNews } from "store/thunks";
import HighlightText from "./HighlightText";

interface BoxVoteKeywordProps {
    children?: React.ReactNode;
    isShowVote?: boolean,
    categories?: any[],
    keyword?: string,
    created_date?: string,
    onSetVoteKeywordInsert?: (item: { image_url?: string, content?: string, time_insert?: number }) => void,
    resetCursorToStart?: () => void,
}

const BoxVoteKeyword: React.ForwardRefRenderFunction<HTMLInputElement, BoxVoteKeywordProps> = ({ children, isShowVote = true, categories, keyword, created_date, onSetVoteKeywordInsert, resetCursorToStart }, ref) => {
    const { t, i18n } = useTranslation();
    const refBoxViewGenerate: any = ref;
    const refBoxViewVotes = useRef<any>([]);
    const refHeaderSticky = useRef<any>(null);

    const [params, setParams] = useState<{
        page: number, limit: number, time_request: number,
        section: string, sort_by: string, order_by: string,
        dc_trend_keyword: string, dc_trend_category_id: string, dc_trend_created_date: string
    }>({
        page: 1, limit: 300, time_request: + new Date(),
        section: `${SECTION_KEYWORD.CONTENT_VOTE_KEYWORD}`,
        sort_by: 'date', order_by: 'DESC',
        dc_trend_keyword: '', dc_trend_category_id: '', dc_trend_created_date: '',
    });

    const [categorySearch, setCategorySearch] = useState<Option | null>(null);
    const [keywordSearch, setKeywordSearch] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [listNews, setListNews] = useState<any[]>([]);

    const [isShow, setIsShow] = useState<boolean>(false);
    const [itemShow, setItemShow] = useState<any | null>(null);

    const [isShowBoxVote, setIsShowBoxVote] = useState<boolean>(false);

    const [idRegenerateLoading, setIdRegenerateLoading] = useState<number | null>(null);

    const [isSticky, setIsSticky] = useState(false);

    const handleSearch = () => {
        setParams((_prev) => ({ ..._prev, time_request: + new Date(), dc_trend_keyword: keywordSearch, dc_trend_category_id: String(categorySearch?.value || '') }));
    };

    const handleToggleBoxVote = () => {
        setIsShowBoxVote((_prev) => !_prev);
    };

    const handleInsertVoteKeyword = (_news: any, type: 'CONTENT' | 'IMAGE' = 'CONTENT') => {

        onSetVoteKeywordInsert && onSetVoteKeywordInsert({
            image_url: type === 'IMAGE' ? (_news?.image_url_select || _news?.source_vote_keyword?.images?.[0]?.image_url) : '',
            content: type === 'CONTENT' ? _news?.body : '',
            time_insert: + new Date(),
        });
        if (type === 'IMAGE') {
            handleSetCloseItemShow();
        }
    };

    const handleRegenerateArticle = async (_item: any) => {
        try {
            if (!!idRegenerateLoading) {
                return;
            }
            setIdRegenerateLoading(_item?.id);
            const response: any = await postReCreateVoteKeywordNews(_item?.id);
            setIdRegenerateLoading(null);
            if (response?.code === 200) {
                const _itemReCreate = { ..._item, body: response?.data?.body };
                setListNews((_prev: any) => {
                    return _prev?.map((_n: any) => (_n?.id === _itemReCreate?.id ? _itemReCreate : _n));
                });
                toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
            } else {
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIdRegenerateLoading(null);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const handleSetOpenItemShow = (_item: any) => {
        setItemShow((_prev: any) => ({
            ..._item,
            image_url_select: _item?.image_url_select || (_item?.source_vote_keyword?.images?.[0]?.image_url || ''),
        }));
        setIsShow((_prev: any) => true);
    };

    const handleSetCloseItemShow = () => {
        setIsShow((_prev: any) => false);
        setItemShow((_prev: any) => null);
    };

    const handleCallAllOption = async (_params: any = {}) => {
        try {
            setIsLoading((_prev) => true);
            const [res1]: any = await Promise.all([getAINewsByNormal(_params)]);
            setIsLoading((_prev) => false);
            setListNews((_prev) => res1?.data?.list || []);
        } catch (error: any) {
            setIsLoading((_prev) => false);
            return error;
        }
    };

    useEffect(() => {
        if (!!keyword && !!created_date && !!isShowVote) {
            setKeywordSearch((_prev) => (keyword || ''));
            setCategorySearch((_prev) => null);
            setParams((_prev) => ({ ..._prev, dc_trend_created_date: created_date || '', dc_trend_keyword: keyword || '', dc_trend_category_id: '' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowVote, keyword, created_date]);

    useEffect(() => {
        if (params?.dc_trend_keyword) {
            handleCallAllOption(params);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(params)]);

    useEffect(() => {
        return () => {
            setListNews(() => []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            const target = event.target as HTMLElement;

            // Check if the click is outside all elements in refBoxViewVotes
            const clickedOutsideImages = refBoxViewVotes.current.every(
                (ref: any) => ref && !ref.contains(target)
            );

            // Check if the click is outside the element in refBoxViewGenerate
            const clickedOutsideGenerate =
                refBoxViewGenerate.current && !refBoxViewGenerate.current.contains(target);

            // Check if the click is inside an element with the class 'tox-dialog' (TinyMCE modal)
            const clickedInsideEditorModal = !!document.querySelector('.tox-dialog')?.contains(target);

            // If the click is outside both refBoxViewVotes and refBoxViewGenerate, and not inside the editor modal
            if (clickedOutsideImages && clickedOutsideGenerate && !clickedInsideEditorModal) {
                resetCursorToStart && resetCursorToStart();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [resetCursorToStart, refBoxViewGenerate, refBoxViewVotes]);


    useEffect(() => {
        const handleScroll = () => {
            if (refHeaderSticky.current) {
                const scrollPosition = refHeaderSticky.current.scrollTop;
                setIsSticky(scrollPosition > 5);
            }
        };

        const element = refHeaderSticky.current;
        if (element) {
            element.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (element) {
                element.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    return (
        <div>
            <div className={`px-0 overflow-x-auto ${isShowVote ? '' : 'd-none'}`} ref={refHeaderSticky} style={{ height: isShowBoxVote ? '60dvh' : '36px', overflowY: isShowBoxVote ? 'auto' : 'hidden', background: '#f4f6fa', backdropFilter: 'blur(10px)', position: 'absolute', bottom: '0px', width: 'calc(100% - 15px)', left: '15px', right: '0px' }}>
                <div className="position-relative" >
                    <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#f4f6fa' }} className={`pb-2 ${isSticky ? 'sticky-box-shadow-bottom' : ''}`} >
                        <div className="w-100 d-flex justify-content-between align-items-center py-1 cursor-pointer" onClick={handleToggleBoxVote} style={{
                            borderBottom: '1px dashed var(--vz-primary)',
                        }}>
                            <span className="text-primary ms-3">{t('DC Trend Body Text')}</span>
                            <span className="text-primary me-1">{isShowBoxVote ? <i className="bx bx-chevrons-down fs-24 " style={{ lineHeight: '27px' }} /> : <i className="bx bx-chevrons-up fs-24 " style={{ lineHeight: '27px' }} />}</span>
                        </div>
                        <div className="mt-2 px-3">
                            <Row className="pt-0">
                                <Col sm={6} className='mt-3'>
                                    <LabelWrapper label={t('Category')} isShow={true}>
                                        <DropdownCategoryVote
                                            name="category_id"
                                            placeholder={t('Category')}
                                            isClearable={true}
                                            dataList={categories}
                                            initialValue={categorySearch}
                                            onChangeCategory={(event) => {
                                                setCategorySearch(event);
                                            }}
                                            className="dropdown-status-rounded"
                                            classNamePrefix="name-prefix"
                                        />
                                    </LabelWrapper>
                                </Col>
                                <Col sm={6} className='mt-3'>
                                    <div className="d-flex gap-4">
                                        <div className="flex-grow-1">
                                            <LabelWrapper label={t('Keyword')} isShow={true}>
                                                <Input
                                                    type="text"
                                                    className="form-control search"
                                                    placeholder={`${t('Keyword')}...`}
                                                    value={keywordSearch}
                                                    onChange={(e) => setKeywordSearch(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            handleSearch();
                                                        }
                                                    }}
                                                />
                                            </LabelWrapper>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleSearch}
                                            disabled={isLoading}
                                        >
                                            <i className="ri-search-line align-bottom"></i>
                                        </button>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </div>
                    <div className="px-3">
                        <div className="d-flex flex-column gap-3 mt-4 pb-4">
                            {listNews?.map((news: any, index: number) => (
                                <Card className={`position-relative border cursor-pointer  border-hover-primary mb-0`} key={news?.id}>
                                    <CardBody style={{ position: 'relative' }} ref={(el: any) => (refBoxViewVotes.current[index] = el)}>
                                        <div className="d-sm-flex" >
                                            <div className="flex-grow-1">
                                                <div className="d-flex mb-3 justify-content-between align-items-center">
                                                    <div>
                                                        <span className="text-muted fs-13">{t('Category')}: {(news?.source_vote_keyword?.categories || [])?.map((_cate: any) => (
                                                            <span className="rounded-pill me-2 badge bg-primary-subtle text-primary fs-11">{_cate?.title || ''}</span>
                                                        ))} </span>
                                                    </div>
                                                    <div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-soft-primary btn-sm w-100 cursor-pointer"
                                                            style={{ borderRadius: '8px' }}
                                                            onClick={() => handleRegenerateArticle(news)}
                                                            disabled={!!idRegenerateLoading && (idRegenerateLoading !== news?.id)}
                                                        >
                                                            <span>{(idRegenerateLoading === news?.id) ? <Spinner size="sm" className="align-middle" style={{ width: '12px', height: '12px' }}></Spinner> : <i className="ri-donut-chart-line align-bottom" />}</span> {t('Regenerate Article')}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <div className="flex-grow-1 box-content-vote-keyword" onClick={() => handleInsertVoteKeyword(news, 'CONTENT')}>
                                                        <HighlightText text={news?.body || ''} keyword={news?.keyword || ''} opacity_bg={0.7} />
                                                    </div>
                                                    <div onClick={(e: any) => { e?.stopPropagation(); e?.preventDefault(); handleSetOpenItemShow(news) }} className="position-relative">
                                                        <img style={{ width: '60px', height: '60px', borderRadius: '6px' }} className="object-fit-cover" src={news?.image_url_select || news?.source_vote_keyword?.images?.[0]?.thumbnail_url || ''} alt="thumbnail news" />
                                                        <Badge pill color="danger" className="position-absolute top-5 start-70 translate-middle">
                                                            {news?.source_vote_keyword?.images?.length || 0}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </div>
                    </div>
                    <div className="text-center my-2 position-absolute " style={{ top: '50%', left: '50%', transform: 'translate(-50%, 100%)' }}>
                        {!!isLoading && <Spinner size="sm" className="text-primary fs-20 align-middle" style={{ width: '30px', height: '30px' }}></Spinner>}
                    </div>
                </div>
            </div>
            <Modal
                isOpen={!!isShow}
                centered={true}
                size="lg"
                scrollable={true}
                toggle={handleSetCloseItemShow}
            >
                <ModalHeader toggle={handleSetCloseItemShow}>
                    <>{t("Image")}</>
                </ModalHeader>
                <ModalBody className="pt-0">
                    <div className="w-100 pt-3 d-flex gap-3 flex-wrap" style={{ minWidth: '100px' }} ref={(el: any) => (refBoxViewVotes.current[listNews?.length + 1] = el)}>
                        {(itemShow?.source_vote_keyword?.images || [])?.map((_img: any, index: number) => (
                            <div className="position-relative">
                                <div style={{ width: '150px', height: '150px' }} key={_img?.id}>
                                    {<img src={_img?.thumbnail_url || ''} className="flex-shrink-0 rounded w-100 h-100 object-fit-cover" alt={_img?.keyword_id} />}
                                </div>
                                <div className="cursor-pointer d-flex justify-content-center align-items-center mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-soft-primary btn-sm w-100 cursor-pointer"
                                        style={{ borderRadius: '8px' }}
                                        onClick={() => handleInsertVoteKeyword({ ...itemShow, image_url_select: _img?.image_url }, 'IMAGE')}
                                    >
                                        {t('Select Image')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="d-flex justify-content-center align-item-center">
                        <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={handleSetCloseItemShow}
                            style={{ width: '100px' }}
                        >
                            <span className="ms-1">{t("Button Close")}</span>
                        </button>
                    </div>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default React.memo(React.forwardRef(BoxVoteKeyword));
